import { useCallback, useEffect, useRef, useState } from 'react';

import { FormikProps } from 'formik';

import {
  getCampaignImages,
  getTemporaryCampaignURL,
  uploadTemporaryCampaignImage,
} from '@pharmaplan/common';

import { resetDialog } from '../../reducers/dialogReducer';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';
import { formKeys } from '../../helpers/Constants';

export interface CampaignImageField {
  key: formKeys;
  label: string;
  recommendedSize: string;
  isEmail: boolean;
}

export interface CampaignImageValue {
  name: string;
  uri: string;
  file?: File | null;
}

const fields: Array<CampaignImageField> = [
  {
    key: formKeys.registrationEmail,
    label: strings.leadInviteEmail,
    recommendedSize: '1200 x 315 px',
    isEmail: true,
  },
  {
    key: formKeys.existingUserEmail,
    label: strings.inviteExistingUser,
    recommendedSize: '1200 x 315 px',
    isEmail: true,
  },
  {
    key: formKeys.activationEmail,
    label: strings.accountActivatedEmailImage,
    recommendedSize: '1200 x 315 px',
    isEmail: true,
  },
  {
    key: formKeys.bookingEmail,
    label: strings.bookingConfirmedEmailImage,
    recommendedSize: '1200 x 315 px',
    isEmail: true,
  },
  {
    key: formKeys.multiBookingEmail,
    label: strings.bookingConfirmedEmailImage,
    recommendedSize: '1200 x 315 px',
    isEmail: true,
  },
];

const MIME_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
};

export function getFileInfoFromUrl(url: string) {
  try {
    if (!url || typeof url !== 'string') {
      return { name: '', type: '' };
    }

    const parsed = new URL(url);
    const { pathname } = parsed;
    const rawName = pathname.substring(pathname.lastIndexOf('/') + 1);
    const cleanName = rawName.split('?')[0].split('#')[0];

    const parts = cleanName.split('_');
    const name = parts.length > 2
      ? parts.slice(-2).join('_')
      : decodeURI(parts[1]) || 'unknown';

    const extMatch = /\.([a-zA-Z0-9]+)$/.exec(name);
    const extension = extMatch ? extMatch[1].toLowerCase() : null;

    const type = (extension && MIME_MAP[extension]) || 'application/octet-stream';

    return { name, type };
  } catch {
    return {
      name: 'unknown',
      type: 'application/octet-stream',
    };
  }
}

const useUploadCampaignImages = (formik: FormikProps<any>) => {
  const dispatch = useAppDispatch();
  const [testEmailKeys, setTestEmailKeys] = useState<Array<formKeys>>([]);

  const currentUploadedImage = useRef<string | null>(null);
  const temporaryCampaignURL = useAppSelector(getTemporaryCampaignURL);

  const images = useAppSelector(getCampaignImages);

  const handleSelect = useCallback(
    (fieldKey: string, file: File) => {
      const formdata = new FormData();
      formdata.append(fieldKey, file);

      currentUploadedImage.current = fieldKey;

      dispatch(uploadTemporaryCampaignImage(formdata));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [images],
  );

  const handleRemove = useCallback(
    (fieldKey: string) =>
      () => {
        formik.setFieldValue(fieldKey, null);
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [images],
  );

  const closeDialog = useCallback(() => {
    dispatch(resetDialog());
  }, [dispatch]);

  useEffect(() => {
    if (temporaryCampaignURL && currentUploadedImage.current) {
      formik.setFieldValue(currentUploadedImage.current, temporaryCampaignURL);

      currentUploadedImage.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporaryCampaignURL]);

  return {
    images,
    fields,
    closeDialog,
    onSelect: handleSelect,
    onRemove: handleRemove,
    testEmailKeys,
    setTestKeyEmailKeys: setTestEmailKeys,
  };
};

export default useUploadCampaignImages;
